/**
 * Raised when a promise handed to {@link withTimeout} does not settle in time.
 * Its own class so a stall reads differently from a real failure — in a
 * `catch`, and in Sentry.
 */
export class TimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`${label} did not settle within ${ms}ms`);
    this.name = 'TimeoutError';
  }
}

/**
 * Resolves with `promise`, or rejects with a {@link TimeoutError} after `ms`.
 *
 * The underlying work is NOT cancelled — a promise cannot be cancelled — it is
 * only no longer awaited. Use this where carrying on without the result beats
 * waiting forever, never where the result is required for correctness.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(label, ms)), ms);
  });
  return Promise.race([promise, expiry]).finally(() => clearTimeout(timer));
}
